import { CHAIN_ID, CHAIN_PARAMS, normalizeChainId } from './connectionConfig';

/**
 * Запросы к кошельку. Каждая функция либо возвращает результат, либо бросает
 * ошибку с текстом для оператора: молча уходить в консоль здесь нельзя —
 * оператор распоряжается реальными деньгами и должен видеть отказ.
 */

const operatorMessage = (error, fallback) => {
  // Коды EIP-1193. У MetaMask код иногда лежит во вложенной ошибке.
  const code = error?.code ?? error?.data?.originalError?.code;

  switch (code) {
    case 4001:
      return 'Запрос отклонён в кошельке';
    case -32002:
      // Самая частая причина «кнопка не работает»: окно MetaMask уже открыто
      // (иногда свёрнуто), и повторный запрос отбрасывается кошельком.
      return 'Запрос уже открыт в MetaMask — подтвердите его в расширении';
    default:
      return `${fallback}: ${error?.message ?? 'неизвестная ошибка'}`;
  }
};

export const requestAccounts = async (provider) => {
  let accounts;

  try {
    accounts = await provider.request({ method: 'eth_requestAccounts' });
  } catch (error) {
    console.error('eth_requestAccounts:', error);
    throw new Error(operatorMessage(error, 'Не удалось подключить кошелёк'));
  }

  if (!accounts || accounts.length === 0) {
    throw new Error('Кошелёк не вернул ни одного аккаунта');
  }

  return accounts;
};

/** Молчаливая проверка: доступ уже выдан или нет. Окно кошелька не открывает. */
export const getAccounts = async (provider) => {
  try {
    return (await provider.request({ method: 'eth_accounts' })) ?? [];
  } catch (error) {
    console.error('eth_accounts:', error);
    return [];
  }
};

export const getChainId = async (provider) => {
  try {
    return normalizeChainId(await provider.request({ method: 'eth_chainId' }));
  } catch (error) {
    console.error('eth_chainId:', error);
    return null;
  }
};

export const switchToTargetChain = async (provider) => {
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: CHAIN_ID }],
    });
  } catch (error) {
    const code = error?.code ?? error?.data?.originalError?.code;

    // 4902 — сети нет в кошельке, её нужно сначала добавить.
    if (code !== 4902) {
      console.error('wallet_switchEthereumChain:', error);
      throw new Error(
        operatorMessage(error, 'Не удалось переключиться на BSC'),
      );
    }

    try {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: CHAIN_PARAMS,
      });
    } catch (addError) {
      console.error('wallet_addEthereumChain:', addError);
      throw new Error(
        operatorMessage(addError, 'Не удалось добавить сеть BSC'),
      );
    }
  }

  // Кошелёк отвечает успехом раньше, чем реально меняет активную сеть, —
  // и «переключились» без проверки означало бы транзакцию в чужой сети.
  const chainId = await getChainId(provider);

  if (chainId !== CHAIN_ID) {
    throw new Error('Сеть в кошельке не переключилась на BSC');
  }

  return chainId;
};

/** Отзыв выданного доступа. Поддерживают не все кошельки — отказ не критичен. */
export const revokeAccountsPermission = async (provider) => {
  try {
    await provider.request({
      method: 'wallet_revokePermissions',
      params: [{ eth_accounts: {} }],
    });
    return true;
  } catch (error) {
    console.warn('wallet_revokePermissions не поддержан:', error?.message);
    return false;
  }
};
